import React, { useState, useEffect, useRef } from 'react';
import $ from 'jquery';
import CreatableSelect from 'react-select/creatable';
import { getApiTeamTagsUrl, getApiPageTagsUrl } from '../../utilities/urlHelper';
import { TAG_SOURCE } from '../../utilities/CONSTANTS';

const AddTagMenu = (props) => {
    const {
        tags, availableTags, pageId, onTagsAdded, closeModal,
    } = props;

    const [existingTags, setExistingTags] = useState(tags); // tags currently attached to the page
    const [tagOptions, setTagOptions] = useState(availableTags || []); // the list of tags in the select dropdown
    const [tagInputValue, setTagInputValue] = useState([]); // 1 or more tags entered by the user in the select dropdown
    const [enableButton, setEnableButton] = useState(false);
    const [buttonText, setButtonText] = useState('Add tag');
    const [tagOptionsLoaded, setTagOptionsLoaded] = useState(false);

    const isMounted = useRef(false);

    useEffect(() => {
        isMounted.current = true;
        $.get(getApiTeamTagsUrl())
            .then(({ tags: fetchedTags }) => {
                if (isMounted.current) {
                    const existingTagsIds = existingTags.map(t => t.id);
                    const newTagOptions = (fetchedTags || [])
                        // exclude added tags from the team pool
                        .filter(({ id, source }) => source === TAG_SOURCE.TEAM && !existingTagsIds.includes(id))
                        // map to a format that CreatableSelect can use
                        .map(({ name }) => ({ label: name, value: name }));
                    setTagOptionsLoaded(true);
                    setTagOptions(newTagOptions);
                }
            })
            .catch(() => {
                if (isMounted.current) {
                    setTagOptionsLoaded(true);
                }
            });

        return () => {
            isMounted.current = false;
        };
    }, [existingTags]);

    // Handle deleting a tag - update the available tags in the dropdown
    useEffect(() => {
        setTagOptions(availableTags.map(({ name }) => ({ label: name, value: name })));
    }, [availableTags, existingTags]);

    const handleTagChange = (newValue) => {
        if (!newValue || newValue.length === 0) {
            setTagInputValue([]);
            setEnableButton(false);
        } else {
            setTagInputValue(newValue);
            setEnableButton(true);
        }
    };

    const addTags = () => {
        if (!enableButton) return;

        setButtonText('Adding...');
        setEnableButton(false);

        const existingTagNames = existingTags.map(t => t.name?.toLowerCase());

        // exclude new tags from existing tags
        const newTags = tagInputValue.filter(tag => !existingTagNames.includes(tag.value?.toLowerCase()));

        // if there are no new unique tags don't do anything
        if (newTags.length === 0) {
            setTagInputValue([]);
            setButtonText('Add tag');
            setEnableButton(false);
            return;
        }

        const tagsToAdd = newTags.map(({ value }) => value);

        $.post(getApiPageTagsUrl(pageId), { tags: tagsToAdd })
            .then((response) => {
                if (isMounted.current) {
                    if (onTagsAdded) onTagsAdded(response.tags);

                    if (response.available_page_tags) {
                        setTagOptions(response.available_page_tags.map(({ name }) => ({ label: name, value: name })));
                        setTagInputValue([]);
                        setButtonText('Add tag');
                        setEnableButton(false);
                    }
                }
            })
            .fail(() => {
                if (isMounted.current) {
                    setButtonText('Add tag');
                    setEnableButton(true);
                }
            });
    };

    const noOptionsText = () => 'No match';

    const formatLabelText = (userInput) => `Add tag: ${userInput}`;

    // Rudimentary tag validation: must be 1-50 characters
    const validateTag = (inputValue) => inputValue.length > 0 && inputValue.length <= 50;

    const btnDisabledClass = enableButton ? '' : 'is-disabled';

    return (
        <div className="mt2">
            <h4>Add tags</h4>
            {!tagOptionsLoaded ? (
                <div className="loading gray">Loading tags</div>
            ) : (
                <CreatableSelect
                    value={tagInputValue}
                    isMulti
                    formatCreateLabel={formatLabelText}
                    noOptionsMessage={noOptionsText}
                    isValidNewOption={validateTag}
                    onChange={handleTagChange}
                    options={tagOptions}
                    placeholder="Search or create tags"
                />
            )}

            <div className="right-align mt2">
                <button
                    type="button"
                    className="btn-secondary mr1"
                    onClick={closeModal}
                >
                    Close
                </button>

                <button
                    type="button"
                    className={`btn btn-primary ${btnDisabledClass}`}
                    onClick={addTags}
                >
                    {buttonText}
                </button>
            </div>
        </div>
    );
};

export default AddTagMenu;
